import React, { lazy, useState, useEffect } from 'react';
import { ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Input, FormGroup, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import GitHubUserCard from './GitHubUserCard';
import { SpinnerLoader } from '../shared/spinnerLoader';
import { SpinnerLoaderType } from '../../Helpers/enums';

const Modal = lazy(() => import('reactstrap/lib/Modal'));
const GITHUB_GRAPHQL_URL = 'https://api.github.com/graphql';
// 🔹 In prod, inject via env var!
const GITHUB_TOKEN = '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';

const USERS_PER_PAGE = 10;

const SearchNewApplicantsModal = ({ isModalOpen, toggleIsModalOpen, job }) => {
  const { t } = useTranslation();

  // --- Search/filter state ---
  const [language, setLanguage] = useState('');
  const [country, setCountry] = useState('');
  const [fullName, setFullName] = useState('');
  const [minFollowers, setMinFollowers] = useState('');
  const [minRepos, setMinRepos] = useState('');
  const [createdAfter, setCreatedAfter] = useState('');
  // const [isHireable, setIsHireable] = useState(false);

  // --- Sorting state ---
  const [sortBy, setSortBy] = useState('followers');
  const [sortOrder, setSortOrder] = useState('desc');
  const [useServerSort, setUseServerSort] = useState(true);

  // --- Pagination & results ---
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [pagesCache, setPagesCache] = useState([]);
  const [cursorsCache, setCursorsCache] = useState([null]);

  // Reset whenever opened
  useEffect(() => {
    if (isModalOpen) {
      setPagesCache([]);
      setCursorsCache([null]);
      setUsers([]);
      setTotalUsers(0);
      setCurrentPage(1);
      setError(null);
      setLanguage('');
      setCountry('');
      setFullName('');
      setMinFollowers('');
      setMinRepos('');
      setCreatedAfter('');
      //setIsHireable(false);
    }
  }, [isModalOpen]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (loading || (!language && !country && !fullName)) return;
    fetchGitHubUsersGraphQl(1);
  };

  async function fetchGitHubUsersGraphQl(page, updateCurrent = true) {
    setLoading(true);
    setError(null);
    const cursorForPage = cursorsCache[page - 1];

    // build qualifiers
    const qualifiers = [
      language && `language:${language}`,
      country && `location:${country}`,
      fullName && `in:name ${fullName}`,
      minFollowers && `followers:>=${minFollowers}`,
      minRepos && `repos:>=${minRepos}`,
      createdAfter && `created:>=${createdAfter}`,
      // isHireable && 'is:hireable',
    ].filter(Boolean);

    // if we want server-side sort, add the qualifier
    if (useServerSort) {
      qualifiers.push(`sort:${sortBy}-${sortOrder}`);
    }

    const searchQuery = qualifiers.join(' ');
    const query = {
      query: `
        {
          search(query: "${searchQuery}", type: USER, first: ${USERS_PER_PAGE}, after: ${
        cursorForPage ? `"${cursorForPage}"` : 'null'
      }) {
            userCount
            pageInfo { endCursor hasNextPage }
            nodes {
              ... on User {
                login name email bio isHireable location company websiteUrl
                twitterUsername avatarUrl url
                followers { totalCount }
                following { totalCount }
                createdAt updatedAt
                repositoriesContributedTo { totalCount }
                repositories(first: 5, orderBy: { field: STARGAZERS, direction: DESC }) {
                  nodes {
                    name description forkCount stargazerCount
                    primaryLanguage { name }
                  }
                }
              }
            }
          }
        }
      `,
    };

    try {
      const res = await fetch(GITHUB_GRAPHQL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${GITHUB_TOKEN}`,
        },
        body: JSON.stringify(query),
      });
      if (!res.ok) throw new Error(`GitHub API error: ${res.status}`);
      const json = await res.json();
      if (!json.data?.search) throw new Error('Unexpected response');

      const fetchedUsers = json.data.search.nodes;
      const fetchedTotal = json.data.search.userCount;
      const { endCursor, hasNextPage } = json.data.search.pageInfo;

      // update caches
      setPagesCache((prev) => {
        const nc = [...prev];
        nc[page - 1] = { users: fetchedUsers, total: fetchedTotal, hasNextPage };
        return nc;
      });
      setCursorsCache((prev) => {
        const cc = [...prev];
        cc[page] = endCursor;
        return cc;
      });

      // client-side sort fallback
      let finalUsers = fetchedUsers;
      if (!useServerSort) {
        finalUsers = [...fetchedUsers].sort((a, b) => {
          const aCount = a[sortBy]?.totalCount ?? 0;
          const bCount = b[sortBy]?.totalCount ?? 0;
          return sortOrder === 'asc' ? aCount - bCount : bCount - aCount;
        });
      }

      if (updateCurrent) {
        setUsers(finalUsers);
        setTotalUsers(fetchedTotal);
        setHasNextPage(hasNextPage);
        setCurrentPage(page);
      }
      return { users: finalUsers, total: fetchedTotal, hasNextPage, endCursor };
    } catch (err) {
      console.error(err);
      setError('Error fetching candidates. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // pagination handler (unchanged)
  const onPaginationSelect = async (newPage) => {
    if (newPage < 1 || newPage > Math.ceil(totalUsers / USERS_PER_PAGE) || newPage === currentPage) return;
    if (pagesCache[newPage - 1]) {
      const { users, total, hasNextPage } = pagesCache[newPage - 1];
      setUsers(users);
      setTotalUsers(total);
      setHasNextPage(hasNextPage);
      setCurrentPage(newPage);
    } else if (newPage > currentPage) {
      let last;
      for (let p = currentPage + 1; p <= newPage; p++) {
        last = await fetchGitHubUsersGraphQl(p, false);
      }
      if (last) {
        setUsers(last.users);
        setTotalUsers(last.total);
        setHasNextPage(last.hasNextPage);
        setCurrentPage(newPage);
      }
    } else {
      // backward from cache
      const { users, total, hasNextPage } = pagesCache[newPage - 1];
      setUsers(users);
      setTotalUsers(total);
      setHasNextPage(hasNextPage);
      setCurrentPage(newPage);
    }
  };

  const totalPages = Math.ceil(totalUsers / USERS_PER_PAGE);

  return (
    <Modal isOpen={isModalOpen} style={{ maxWidth: '900px' }} centered>
      <ModalHeader>
        <h3 className="d-flex align-items-center">
          <i className="fas fa-search-plus mr-2" />
          {t('jobs.SEARCH_CANDIDATES')} - {job?.title}
        </h3>
      </ModalHeader>

      <ModalBody style={{ maxHeight: 800, overflowY: 'auto' }}>
        <form onSubmit={handleSearchSubmit}>
          <FormGroup>
            <Row form>
              <Col md={4}>
                <Label for="languageInput">Language</Label>
                <Input
                  id="languageInput"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  placeholder="e.g. C++"
                />
              </Col>
              <Col md={4}>
                <Label for="countryInput">Country</Label>
                <Input
                  id="countryInput"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="e.g. Slovenia"
                />
              </Col>
              <Col md={4}>
                <Label for="fullNameInput">Name</Label>
                <Input
                  id="fullNameInput"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g. Mike"
                />
              </Col>
            </Row>
          </FormGroup>

          <FormGroup>
            <Row form>
              <Col md={3}>
                <Label for="minFollowersInput">Min Followers</Label>
                <Input
                  id="minFollowersInput"
                  type="number"
                  value={minFollowers}
                  onChange={(e) => setMinFollowers(e.target.value)}
                  placeholder="e.g. 100"
                />
              </Col>
              <Col md={3}>
                <Label for="minReposInput">Min Repos</Label>
                <Input
                  id="minReposInput"
                  type="number"
                  value={minRepos}
                  onChange={(e) => setMinRepos(e.target.value)}
                  placeholder="e.g. 2"
                />
              </Col>
              <Col md={3}>
                <Label for="createdAfterInput">Created After</Label>
                <Input
                  id="createdAfterInput"
                  type="date"
                  value={createdAfter}
                  onChange={(e) => setCreatedAfter(e.target.value)}
                />
              </Col>
              {/* <Col md={3} className="d-flex align-items-center">
                <Label check>
                  <Input type="checkbox" checked={isHireable} onChange={() => setIsHireable(!isHireable)} /> Hireable
                </Label>
              </Col> */}
            </Row>
          </FormGroup>

          <hr />

          <FormGroup>
            <Row form>
              <Col md={4}>
                <Label for="sortBySelect">Sort by</Label>
                <Input type="select" id="sortBySelect" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="followers">Followers</option>
                  <option value="repositories">Repositories</option>
                  <option value="joined">Joined date</option>
                </Input>
              </Col>
              <Col md={4}>
                <Label for="sortOrderSelect">Order</Label>
                <Input
                  type="select"
                  id="sortOrderSelect"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </Input>
              </Col>
              {/* <Col md={4} className="d-flex align-items-center">
                <Label check>
                  <Input type="checkbox" checked={useServerSort} onChange={() => setUseServerSort(!useServerSort)} />{' '}
                  Use server-side sorting
                </Label>
              </Col> */}
            </Row>
          </FormGroup>
        </form>

        {loading ? (
          <SpinnerLoader loaderType={SpinnerLoaderType.Small} />
        ) : (
          <>
            {error && <p className="text-danger text-center">{error}</p>}
            {totalUsers > 0 && (
              <p className="text-center">
                <strong>Found {totalUsers} matching developers</strong>
              </p>
            )}
            {users.length > 0 && (
              <Row>
                {users.map((user) => (
                  <GitHubUserCard key={`${user.login}_${user.createdAt}`} user={user} jobId={job?.id} />
                ))}
              </Row>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        {totalUsers > 0 && (
          <div className="w-100 d-flex justify-content-center align-items-center mb-2">
            <Button
              color="secondary"
              disabled={currentPage === 1 || loading}
              onClick={() => onPaginationSelect(currentPage - 1)}
            >
              &lt;
            </Button>
            <span className="mx-3">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              color="secondary"
              disabled={!hasNextPage || loading}
              onClick={() => onPaginationSelect(currentPage + 1)}
            >
              &gt;
            </Button>
          </div>
        )}
        <Button
          color="primary"
          onClick={() => fetchGitHubUsersGraphQl(1)}
          disabled={loading || (!language && !country && !fullName)}
        >
          {loading ? 'Searching...' : t('common.SEARCH')}
        </Button>
        <Button color="secondary" onClick={toggleIsModalOpen}>
          {t('common.CLOSE')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SearchNewApplicantsModal;
